import { Instant, LocalDate, LocalDateTime } from "@js-joda/core";
import { Grid, VStack } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { auth } from "../../../../../core/auth";
import { env } from "../../../../../env";
import useApi from "../../../../../shared/hooks/useApi";
import { AgencyBranchId } from "../../../../../shared/schema/schema";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import { PatientProfileDisplayData } from "../../../patientProfileModal.mocks";
import { PatientProfilePatient } from "../../../patientProfileModal.types";
import PatientProfileEditModeButton from "../../../../entityProfileModal/components/EntityProfileEditModeButton";
import { useCaregiversQuery } from "../../../../caregiver/hooks/useCaregiversQuery";
import {
  EntityProfileSummaryBlock as SummaryBlock,
  EntityProfileSummaryRow as SummaryRow,
} from "../../../../entityProfileModal/components/EntityProfileSummary";
import { usePatientProfileSummaryController } from "./PatientProfileSummarySection.hooks";
import type { PatientProfileBridgeActions } from "../../../patientProfileModal.actions";
import {
  PatientProfileSummaryContactInformation,
  PatientProfileSummaryServices,
  PatientProfileSummaryStatus,
} from "./components";
import { PersonalDetails } from "./components/PersonalDetails";

type Props = {
  actions: PatientProfileBridgeActions;
  displayData: PatientProfileDisplayData;
  patient: PatientProfilePatient;
  showBillingFeature: boolean;
};

function formatSummaryValue(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (value instanceof LocalDate) {
    return dateFormatter.toDate(value);
  }

  if (value instanceof LocalDateTime || value instanceof Instant) {
    return dateFormatter.toDateTime(value);
  }

  return String(value);
}

function formatBranchesWithOptions(
  agencyBranches: PatientProfilePatient["agencyBranches"],
  branchIds: PatientProfilePatient["agencyBranchIds"],
  branchOptions: Array<{ value: number; label: string }>
) {
  if (branchIds.length === 0) {
    return "";
  }

  const branchNamesById = new Map((agencyBranches ?? []).map((branch) => [branch.id, branch.name]));

  return branchIds
    .map(
      (branchId) =>
        branchNamesById.get(branchId) ??
        branchOptions.find((option) => option.value === branchId)?.label ??
        `Branch #${branchId}`
    )
    .join(", ");
}

function formatAgencyTeamName(
  agencyTeamName: PatientProfilePatient["agencyTeamName"],
  agencyTeamId: PatientProfilePatient["agencyTeamId"],
  teams: Array<{ id: number; name: string }>
) {
  if (agencyTeamName !== null && agencyTeamName !== undefined && agencyTeamName !== "") {
    return agencyTeamName;
  }

  if (agencyTeamId === null) {
    return "";
  }

  return teams.find((team) => team.id === agencyTeamId)?.name ?? `Team #${agencyTeamId}`;
}

function getAgencyTeamOptions(
  agencyTeams: Array<{ id: number; name: string }>,
  selectedAgencyTeamId: PatientProfilePatient["agencyTeamId"]
) {
  const options = agencyTeams.map((team) => ({ value: team.id, label: team.name }));

  if (
    selectedAgencyTeamId !== null &&
    !options.some((option) => option.value === selectedAgencyTeamId)
  ) {
    return [{ value: selectedAgencyTeamId, label: `Team #${selectedAgencyTeamId}` }, ...options];
  }

  return options;
}

export default function PatientProfileSummarySection(props: Props) {
  const { displayData, patient } = props;
  const { queries } = useApi();
  const authState = auth.getAuthState();
  const agencyId = authState.type === "authenticated" ? authState.data.agency.id : null;
  const isOnboardingAgency = agencyId === env.MEDFLYT_AGENCY_ID;
  const blacklistedCaregivers = patient.blacklisted ?? [];
  const shouldShowEligibility = patient.status !== "DRAFT" && patient.status !== "LOST";
  const canEdit = auth.isAllowedTo("edit_patient_page_summary_details");
  const canRunEligibilityCheck = auth.isAllowedTo("run_eligibility_check_for_patient");
  const summaryController = usePatientProfileSummaryController({
    actions: props.actions,
    canEdit,
    patient,
  });
  const {
    activeDraft,
    activeField,
    addressAutocompleteDraft,
    cancelField,
    commitField,
    deletePhoneNumberMutation,
    handleDeletePhoneNumber,
    isContactInformationEditing,
    isPersonalDetailsEditing,
    isServicesInformationEditing,
    isStatusEditing,
    isSummaryEditing,
    openAddPhoneNumber,
    openAddressPicker,
    openCertificationPeriod,
    openChangeStatus,
    runEligibilityCheckMutation,
    setAddressAutocompleteDraft,
    summaryDraft,
    toggleSummaryEditing,
    updateField,
    updateBlacklistMutation,
    updatePrimaryAddressMutation,
    activateField,
  } = summaryController;
  const agencyTeamsQuery = useQuery(queries.agencyTeam.list());
  const caregiversQuery = useCaregiversQuery();
  const agencyBranchesQuery = useQuery({
    ...queries.officeBranch.list(),
  });
  const agencyTeamName = formatAgencyTeamName(
    patient.agencyTeamName,
    patient.agencyTeamId,
    agencyTeamsQuery.data?.agencyTeams ?? []
  );
  const agencyTeamOptions = getAgencyTeamOptions(
    agencyTeamsQuery.data?.agencyTeams ?? [],
    patient.agencyTeamId
  );
  const agencyBranchOptions =
    agencyBranchesQuery.data?.list.map((branch) => ({
      value: AgencyBranchId.parse(branch.id),
      label: branch.name,
    })) ?? [];
  const agencyBranchesText = formatBranchesWithOptions(
    patient.agencyBranches,
    patient.agencyBranchIds,
    agencyBranchOptions
  );
  const blacklistedCaregiversText =
    patient.blacklistedCaregivers?.map((caregiver) => caregiver.displayName).join(", ") ?? "";

  return (
    <VStack alignItems="stretch" spacing={4}>
      {canEdit ? (
        <PatientProfileEditModeButton
          isEditing={isSummaryEditing}
          label="Edit"
          onClick={toggleSummaryEditing}
        />
      ) : null}
      <Grid gap={8} templateColumns={{ base: "1fr", xl: "repeat(2, minmax(0, 1fr))" }}>
        <VStack alignItems="stretch" spacing={8}>
          <PatientProfileSummaryStatus
            isEditing={isStatusEditing}
            patient={patient}
            onChangeStatus={openChangeStatus}
          />
          <PersonalDetails
            activeDraft={activeDraft}
            activeField={activeField}
            addressAutocompleteDraft={addressAutocompleteDraft}
            agencyId={agencyId}
            displayData={displayData}
            isEditing={isPersonalDetailsEditing}
            isOnboardingAgency={isOnboardingAgency}
            patient={patient}
            showBillingFeature={props.showBillingFeature}
            summaryDraft={summaryDraft}
            updatePrimaryAddressMutation={updatePrimaryAddressMutation}
            onActivateField={activateField}
            onCancelField={cancelField}
            onCommitField={commitField}
            onCustomizeAddress={openAddressPicker}
            onSetAddressAutocompleteDraft={setAddressAutocompleteDraft}
            onUpdateField={updateField}
          />
        </VStack>

        <VStack alignItems="stretch" spacing={8}>
          <PatientProfileSummaryContactInformation
            isDeletingPhoneNumber={deletePhoneNumberMutation.isPending}
            isEditing={isContactInformationEditing}
            patient={patient}
            onAddPhoneNumber={openAddPhoneNumber}
            onDeletePhoneNumber={handleDeletePhoneNumber}
          />
          <PatientProfileSummaryServices
            actions={props.actions}
            activeDraft={activeDraft}
            activeField={activeField}
            agencyBranchesText={agencyBranchesText}
            agencyBranchOptions={agencyBranchOptions}
            agencyTeamName={agencyTeamName}
            agencyTeamOptions={agencyTeamOptions}
            blacklistedCaregivers={blacklistedCaregivers}
            blacklistedCaregiversText={blacklistedCaregiversText}
            canRunEligibilityCheck={canRunEligibilityCheck}
            caregiverOptions={caregiversQuery.data ?? []}
            displayData={displayData}
            isAgencyBranchesLoading={agencyBranchesQuery.isLoading}
            isAgencyTeamsLoading={agencyTeamsQuery.isLoading}
            isBlacklistLoading={caregiversQuery.isLoading}
            isBlacklistSaving={updateBlacklistMutation.isPending}
            isEligibilityCheckPending={runEligibilityCheckMutation.isPending}
            isServicesInformationEditing={isServicesInformationEditing}
            patient={patient}
            shouldShowEligibility={shouldShowEligibility}
            summaryDraft={summaryDraft}
            onActivateField={activateField}
            onCancelField={cancelField}
            onCommitField={commitField}
            onOpenCertificationPeriod={openCertificationPeriod}
            onRunEligibilityCheck={() => runEligibilityCheckMutation.mutate()}
            onUpdateBlacklist={(caregiverIds) => updateBlacklistMutation.mutate(caregiverIds)}
            onUpdateField={updateField}
          />

          <SummaryBlock title="Activity">
            <SummaryRow label="Created At" value={formatSummaryValue(patient.createdAt)} />
            <SummaryRow
              label="Created By"
              value={
                patient.createdByName ??
                (patient.createdBy ? `Agency member #${patient.createdBy}` : "")
              }
            />
          </SummaryBlock>
        </VStack>
      </Grid>
    </VStack>
  );
}
